// Dutch UI copy for the SPA. Server-side validation messages are localized
// separately by Rails (config/locales/nl/*). Informal "je" register, for a modern
// Dutch SaaS. Keep copy lean: a strong heading, functional labels, and a supporting
// line only where it tells the user something the form doesn't already.
//
// Two different registers on purpose. The `landing` block (and the two public
// marketing pages) speaks VvE: VvE-bijdrage, appartementseigenaar, machtiging,
// penningmeester, beheerder — that is the category Pandig is sold in and searched
// for, and generic "organisatie" language cost us both recognition and relevance.
// The signed-in product below it stays generic ("organisatie", "eenheid") because
// the data model is: an Organisation with Units, which a non-VvE could use too.
// Never claim more in the marketing block than the code actually does.
export default {
  errors: {
    generic: "Er ging iets mis. Probeer het opnieuw.",
  },
  // The marketing page, as one argument told across ten scroll-snapped pages:
  // recognisable pain (hero, problem) → what it costs the VvE (stakes) → the
  // mechanism (how) → where the money actually goes (promise/trust) → what you see
  // when something goes wrong (proof) → who it's for (audiences) → price → the
  // objections that block a purchase (faq) → the ask (cta). Every page has to earn
  // its screen: no page repeats another page's argument.
  landing: {
    title: "VvE-bijdragen automatisch incasseren",
    description: "Nog elke maand controleren wie de VvE-bijdrage heeft betaald? Pandig automatiseert de incasso, rechtstreeks naar de eigen rekening van de VvE.",
    nav: {
      how: "Hoe het werkt",
      flows: "De geldstroom",
      pricing: "Prijs",
      faq: "Vragen",
      managers: "Voor beheerders",
      sign_in: "Inloggen",
      sign_up: "Account aanmaken",
      menu: "Menu",
      close_menu: "Menu sluiten",
    },
    // Short names for the right-edge page-dot pager, one per full-screen section —
    // gives each dot a real aria-label/title instead of "Ga naar pagina N", and the
    // hovered/active dot shows the label so the pager reads as a table of contents.
    pager: {
      hero: "Home",
      problem: "Het probleem",
      stakes: "Waarom het telt",
      how: "Hoe het werkt",
      flows: "De geldstroom",
      proof: "Wat je ziet",
      audiences: "Voor wie",
      pricing: "Prijs",
      faq: "Vragen",
      cta: "Aan de slag",
    },
    hero: {
      headline: "Niet meer achter VvE-bijdragen aan.",
      // Problem first, mechanism second, money-path last — in that order. Kept to
      // one tight paragraph: the hero is a plain panel that has to fit a phone
      // screen, header included, and this is the copy with the least room to spare.
      lead: "Elke maand nakijken wie de VvE-bijdrage heeft betaald, en dan een buur herinneren aan een vergeten overboeking. Pandig incasseert de bijdragen automatisch, rechtstreeks naar de eigen rekening van de VvE.",
      // The low-commitment route is the primary button and jumps to the mechanism;
      // signing up stays available right next to it, for whoever is already sold.
      cta: "Bekijk hoe een maand loopt",
      cta_secondary: "Start met je VvE",
      managers_q: "Beheer je meerdere VvE's?",
      managers_link: "Bekijk Pandig voor beheerders",
      facts: {
        own: "Rechtstreeks naar de eigen rekening van de VvE",
        sepa: "Met een geldige SEPA-machtiging",
        price: "€ 1 per actieve eenheid per maand, excl. btw",
      },
    },
    flow: {
      label: "Onderweg naar de VvE",
      from_label: "38 eigenaren",
      to_label: "eigen rekening",
      to_iban: "NL·· ···· ··21",
      landed_suffix: "binnen",
      pending_suffix: "onderweg",
      via_label: "via Pandig",
      via_value: "€ 0,00",
      untouched: "Pandig raakt het geld niet aan",
    },
    problem: {
      title: "Wie heeft betaald? Wie nog niet? En wie gaat erachteraan?",
      lead: "Zolang alles goed gaat, lijkt het innen van de bijdragen eenvoudig. Het werk begint bij de uitzonderingen: een eigenaar vergeet de overboeking, een machtiging ontbreekt, een incasso wordt teruggeboekt, of een bedrag is niet te herleiden tot het juiste appartement.",
      card1_title: "De bankrekening is geen betaaloverzicht",
      card1_body: "Bankregels vertellen niet vanzelf welke eenheid klaarstaat voor de volgende incasso en waar iets ontbreekt. De penningmeester of beheerder maakt dat overzicht elke maand opnieuw.",
      card2_title: "Een achterstand begint stil",
      card2_body: "Eén mislukte betaling lijkt klein. Ziet niemand het meteen, dan wordt één maand al snel twee — en wordt het opvolgen ongemakkelijker.",
      card3_title: "Van buur naar debiteur",
      card3_body: "Vrijwillig bestuurder zijn is al genoeg werk. Een buur moeten aanspreken op een betaling maakt een administratieve taak onnodig persoonlijk.",
    },
    // The one cinematic beat on the page: the consequence, lit word by word as you
    // scroll. Deliberately about the VvE's own responsibility, not about Pandig.
    stakes: {
      line: "Als bijdragen achterlopen, loopt meer achter dan de administratie.",
      sub: "Uit de gezamenlijke bijdragen betaalt de VvE de verzekering, het schoonmaken, reparaties en de reservering voor toekomstig onderhoud. Daarom wil een bestuur kunnen vertrouwen op één actueel beeld: wat is binnen, wat komt eraan, en waar is actie nodig?",
    },
    how: {
      title: "De gewone maand loopt vanzelf. Alleen uitzonderingen vragen aandacht.",
      step1_n: "01",
      step1_title: "Eén keer toestemming",
      step1_body: "De eigenaar geeft eenmalig een SEPA-machtiging af via een verificatiebetaling van één cent. Zonder geldige machtiging zet Pandig geen incasso op.",
      step2_n: "02",
      step2_title: "Volgens het afgesproken ritme",
      step2_body: "Pandig incasseert de vastgestelde VvE-bijdrage per maand, kwartaal, half jaar of jaar — per eenheid in te stellen.",
      step3_n: "03",
      step3_title: "Uitzonderingen regelt Pandig",
      // Kort houden: deze pagina moet op een telefoonscherm passen (Landing.spec meet dat).
      step3_body: "Vóór de incasso zie je wat er verwacht wordt. Lukt een betaling niet, dan mailt Pandig de eigenaar en biedt de bijdrage een paar werkdagen later automatisch nog één keer aan.",
    },
    promise: {
      title: "Pandig raakt het geld niet aan",
      lead: "Er lopen twee gescheiden geldstromen. De bijdragen gaan rechtstreeks van de appartementseigenaar naar de eigen rekening van de VvE. Aan Pandig betaalt de VvE alleen een klein bedrag per eenheid.",
      dues_title: "De VvE-bijdragen",
      dues_body: "Van de eigenaar rechtstreeks naar de rekening van de VvE — via een Mollie-account dat op naam van de VvE staat. Pandig zit er niet tussen en houdt geen bijdragen onder zich.",
      dues_from: "eigenaar",
      dues_to: "rekening VvE",
      divider: "Apart gehouden",
      fee_title: "Onze vergoeding",
      // No euro figure in this section's copy on purpose: LandingFlows.spec counts
      // the money readouts inside #geldstroom, and the price has its own page.
      fee_body: "Eén vast bedrag per actieve eenheid per maand, met een btw-factuur. Dit is de enige betaling die naar Pandig gaat.",
      fee_from: "de VvE",
      fee_to: "Pandig",
    },
    trust: {
      mollie_label: "Betaalinfrastructuur",
      mollie: "Machtigingen en incasso's lopen via Mollie B.V., een Nederlandse betaaldienstverlener die onder toezicht staat van De Nederlandsche Bank.",
      mandate_label: "Machtiging",
      mandate: "Elke eigenaar geeft eenmalig toestemming via een verificatiebetaling van één cent. Pandig zet geen incasso op zonder geldige machtiging.",
    },
    proof: {
      title: "Uitzonderingen vallen op. De rest hoeft geen aandacht.",
      lead: "Drie dingen die het innen rustig houden.",
      hint: "Scroll om te bladeren",
      foresight_title: "Zie de run vooraf",
      foresight_body: "Vóór de incasso staat er wat er verwacht wordt, welke machtigingen ontbreken en welke eenheden nog niet kunnen innen.",
      failure_title: "Mislukt? Pandig meldt het",
      failure_body: "Lukt een afschrijving niet, dan mailt Pandig de eigenaar erover en biedt de bijdrage een paar werkdagen later automatisch nog één keer aan — jij hoeft geen buur aan te spreken. Bij Opvolgen staat de eenheid, de eigenaar en wat er al is gedaan; wil je niet wachten, dan bied je zelf met één klik aan.",
      calm_title: "Rust voor bestuur én eigenaar",
      calm_body: "De eigenaar ziet zijn eigen bedrag, machtiging en betalingen. Het bestuur ziet per eenheid of alles klopt. Niemand hoeft te bellen om dat te weten.",
    },
    audiences: {
      title: "Één VvE of een hele portefeuille",
      board_title: "Bestuurder naast je gewone werk?",
      board_body: "Stop met bankmutaties afvinken en losse lijstjes bijhouden. Zie per appartement of de bijdrage, de machtiging en de betaling in orde zijn. Eigenaren zien zelf hun bedrag, machtiging en betaalhistorie.",
      board_cta: "Start met je VvE",
      manager_title: "Meer VvE's beheren zonder meer betaalwerk",
      manager_body: "Werk met dezelfde incassostroom voor iedere VvE, wissel binnen één account tussen organisaties en houd het geld per VvE op de eigen rekening. Je team ziet per VvE waar aandacht nodig is, zonder elke bankrekening langs te gaan.",
      manager_cta: "Bekijk Pandig voor beheerders",
    },
    pricing: {
      title: "Eén simpele prijs",
      lead: "Dat is de enige vergoeding die naar Pandig gaat. De bijdragen van de eigenaren blijven volledig van de VvE — daar nemen we geen commissie op.",
      // Hand-synced with PANDIG_PLATFORM_FEE_CENTS / PANDIG_VAT_RATE
      // (app/models/pandig/configuration.rb) — update these two strings too if that fee changes.
      amount: "€ 1,00",
      unit: "per eenheid, per maand · excl. btw",
      example: "Bijvoorbeeld: 38 eenheden = € 38,00 per maand",
      fees_title: "Transactiekosten",
      fees_body: "De incassokosten van Mollie lopen via de eigen Mollie-overeenkomst van de VvE. Daar zet Pandig geen opslag op.",
      terms_title: "Maandelijks opzegbaar",
      terms_body: "Geen opstartkosten en geen minimumbedrag. Je betaalt per actieve eenheid, en je zegt zelf op in de instellingen — de incasso's stoppen meteen en het abonnement loopt tot het einde van de maand die je al betaald hebt.",
      managers_note: "Beheer je meerdere VvE's? Dan kijken we samen wat past.",
      contact: "Neem contact op",
    },
    faq: {
      title: "Veelgestelde vragen",
      q1: "Waar komt de VvE-bijdrage terecht?",
      a1: "Op een bankrekening die op naam van de VvE staat. De VvE krijgt bij Mollie een eigen account; de incasso's van de eigenaren lopen daarop en worden vanaf daar uitbetaald. Pandig zet de incasso's klaar, maar kan geen geld van dat account halen, niets terugboeken en de uitbetalingen niet inzien.",
      q2: "Wat gebeurt er als een incasso mislukt of wordt teruggeboekt?",
      a2: "Een mislukte incasso zie je meteen bij Opvolgen, met de eenheid en de eigenaar erbij, en Pandig mailt de eigenaar er zelf over — één nette melding, geen aanmaningstraject, en het bestuur hoeft die boodschap niet te brengen. Is de machtiging nog geldig, dan biedt Pandig de bijdrage een paar werkdagen later automatisch nog één keer aan; het bestuur kan dat met één klik ook eerder doen. Het blijft bij die ene extra poging, zodat er nooit herhaald op een lege rekening wordt geïncasseerd — daarna is het aan het bestuur. Ook een terugboeking (storno) na een geslaagde incasso komt bij Opvolgen te staan zodra Mollie die doorgeeft; die wordt nooit automatisch opnieuw aangeboden, want de eigenaar heeft het geld zelf teruggehaald. Omdat een storno een paar dagen kan duren, houdt Pandig een geslaagde incasso zes werkdagen op voorlopig binnen — reken die pas daarna als definitief. Wat er daarna nog nodig is, bepaalt het bestuur of de beheerder: formele incasso van een achterstand doet de VvE zelf of een geregistreerde incassopartner.",
      q3: "Hoe geeft een eigenaar toestemming?",
      a3: "Met één verificatiebetaling van één cent ondertekent de eigenaar een SEPA-machtiging bij Mollie. Zolang die betaling nog niet is verwerkt staat de machtiging op 'wordt geverifieerd' — en zonder geldige machtiging zet Pandig geen incasso op.",
      q4: "Kan een beheerder meerdere VvE's gebruiken?",
      a4: "Ja. Eén account, meerdere VvE's: je wisselt van organisatie zonder opnieuw in te loggen, en elke VvE houdt haar eigen rekening, eigen eenheden en eigen machtigingen. Collega's nodig je per VvE uit, als bestuur of als eigenaar.",
      q5: "Vervangt Pandig onze boekhouding of VvE-beheerder?",
      a5: "Nee. Pandig automatiseert de terugkerende bijdrage, de machtiging en de betaalstatus. Begroting, jaarrekening, MJOP, vergaderingen en technisch beheer blijven bij jullie of bij jullie beheerder.",
      q6: "Welke kosten komen boven op € 1 per eenheid?",
      a6: "Btw, en de transactiekosten die Mollie voor de incasso's rekent — die lopen via de eigen Mollie-overeenkomst van de VvE. Pandig zet daar geen opslag op, neemt geen commissie over de bijdragen en rekent geen opstartkosten of minimumbedrag.",
      q7: "Hoe wijzigen we de bijdrage na een besluit van de vergadering?",
      a7: "Elke eenheid heeft haar eigen bijdrage en ritme, dus een besluit van de vergadering voer je per eenheid door. Het nieuwe bedrag geldt vanaf de volgende incasso: een al lopende incasso op die eenheid wordt daarvoor gestopt en opnieuw aangezet. De betaalhistorie van de eenheid blijft staan.",
    },
    cta: {
      title: "Nog één maand zelf controleren wie heeft betaald?",
      body: "Zet de bijdragen van je VvE in één keer goed klaar: verbind de VvE met Mollie, voeg de eenheden toe en laat elke eigenaar eenmalig toestemming geven. Daarna zie je alleen nog wat aandacht nodig heeft.",
      primary: "Start met je VvE",
      secondary: "Neem contact op",
    },
    contact: {
      hint: "WhatsApp heeft de voorkeur, maar bellen of mailen kan ook.",
      whatsapp: "WhatsApp",
      call: "Bel ons",
      email: "Mail ons",
    },
    footer: {
      tagline: "VvE-bijdragen innen op de eigen rekening van de VvE.",
      product: "Product",
      how: "Hoe het werkt",
      flows: "De geldstroom",
      pricing: "Prijs",
      faq: "Vragen",
      managers: "Voor beheerders",
      arrears: "Betalingsachterstand",
      account: "Account",
      sign_in: "Inloggen",
      sign_up: "Account aanmaken",
      terms: "Algemene voorwaarden",
      privacy: "Privacybeleid",
      cookies: "Cookievoorkeuren",
    },
  },
  // The two public content pages. One search intent each (see robots.txt/sitemap.xml):
  // the manager page owns "VvE software voor beheerders", the arrears page owns
  // "VvE betalingsachterstand". Their body copy lives in the .vue files, like the
  // legal pages — only the tab title and meta description are i18n'd here.
  beheerders: {
    title: "Pandig voor VvE-beheerders",
    description: "Meer VvE's beheren zonder meer betaalwerk: dezelfde incassostroom voor iedere VvE, één account om tussen VvE's te wisselen, en het geld blijft per VvE op de eigen rekening.",
  },
  achterstand: {
    title: "Betalingsachterstand in de VvE",
    description: "Een achterstand in de VvE begint stil. Zo zie je een mislukte incasso vroeg, zo leg je een collectief betaalbeleid vast, en zo ver mag je zelf gaan met innen.",
  },
  voorwaarden: {
    title: "Algemene voorwaarden",
    description: "De algemene voorwaarden van Pandig, de dienst waarmee organisaties periodieke bijdragen automatisch incasseren op hun eigen rekening.",
  },
  privacybeleid: {
    title: "Privacybeleid",
    description: "Hoe Pandig omgaat met persoonsgegevens: welke gegevens we verwerken, waarom, en wie verantwoordelijk is.",
  },
  verwerkersovereenkomst: {
    title: "Verwerkersovereenkomst",
    description: "De verwerkersovereenkomst van Pandig voor organisaties waarvoor Pandig als verwerker persoonsgegevens verwerkt.",
  },
  cookies: {
    eyebrow: "Cookies",
    // Written in the persona's own nouns rather than about them — bijdrage,
    // machtiging, innen, bankmutaties afvinken all come straight out of PERSONAS.md.
    // The card is mounted globally (App.vue), so all three personas read it, and
    // PERSONAS.md's priority sets the order: the vrijwillige bestuurder first, the
    // beheerder alongside, the eigenaar through what the necessary row protects.
    //
    // Saying what a yes buys is the one lever that may push towards consent. What may
    // not: preselecting the switches, or making either all-in-one button louder than
    // the other (CookieNotice.vue explains why). So the sentences work, the interface
    // stays neutral.
    //
    // Kept to roughly the length of the sentence it replaced: the card is `fixed` and
    // on a 390x640 phone two extra lines push it over the hero's own CTA
    // (Landing.spec.js catches exactly that).
    body: "Pandig B.V. gebruikt strikt noodzakelijke cookies om je veilig in te laten loggen bij je VvE; die staan altijd aan en meten niets. Statistieken via Google en advertentiecookies staan uit tot jij ze aanzet — daarmee zien we waar een bestuur vastloopt in onze uitleg. Weigeren heeft geen gevolgen.",
    link: "Meer info in ons privacybeleid",
    standing: "Dit zijn je huidige keuzes. Pas ze aan en sla ze op.",
    essential_label: "Noodzakelijk",
    // Every row is on screen at once, on a phone too, so each description is held to
    // about two lines there. What may not be cut to get there: the purpose, that the
    // meting is public-website-only, the named recipient, and that no advertentietag
    // exists yet. What was cut is the second telling of each. Privacybeleid §5 carries
    // the long form, one tap away under the card's own link.
    essential_description: "Inloggen, beveiliging en sessiebeheer. Zonder deze kun je niet veilig bij je VvE, dus ze staan altijd aan.",
    statistics_label: "Statistieken",
    statistics_description: "Google Analytics 4, via Google Tag Manager en alleen op deze openbare website — nooit in de omgeving van je VvE. Zo zien we waar onze uitleg over innen afhaakt. Eén derde partij: Google Ireland Limited.",
    marketing_label: "Marketing",
    marketing_description: "Toestemming voor advertentiecookies, om besturen te bereiken die hun bijdragen nog met bankmutaties afvinken. Er is nu geen advertentietag ingericht; deze schakelaar bepaalt of dat later mag.",
    decline: "Alles weigeren",
    accept: "Alles accepteren",
    customise: "Zelf kiezen",
    save: "Mijn keuze opslaan",
  },
  signin: {
    title: "Inloggen",
    heading: "Welkom terug",
    email: "E-mailadres",
    email_ph: "naam{'@'}voorbeeld.nl",
    password: "Wachtwoord",
    submit: "Inloggen",
    forgot: "Wachtwoord vergeten?",
    no_account: "Nog geen account?",
    register: "Account aanmaken",
    otp_heading: "Verificatiecode",
    otp_lead: "Voer de 6-cijferige code uit je authenticator-app in, of gebruik een van je back-upcodes.",
    otp_code: "Verificatiecode",
    otp_code_ph: "123456",
    otp_submit: "Bevestigen",
    otp_back: "Terug naar inloggen",
    or_divider: "of",
    passkey_submit: "Inloggen met een passkey",
  },
  signup: {
    title: "Account aanmaken",
    heading: "Maak je account",
    organisation: "Organisatie",
    organisation_ph: "bijv. Parkflat Zuidas",
    name: "Naam",
    email: "E-mailadres",
    email_ph: "naam{'@'}voorbeeld.nl",
    password: "Wachtwoord",
    password_hint: "Minimaal 15 tekens.",
    password_confirmation: "Herhaal wachtwoord",
    submit: "Account aanmaken",
    has_account: "Heb je al een account?",
    sign_in: "Inloggen",
    // The animated registration journey. One decision per screen, tailored to who is
    // registering (bestuur die met de eigen VvE start, of beheerder die de eerste van
    // een portefeuille aanmeldt). The field labels above stay the source of truth —
    // journey copy sits on top of them, never replaces them.
    journey: {
      step_of: "Stap {current} van {total}",
      next: "Volgende",
      back: "Vorige",
      // Short labels for the step tracker.
      steps: {
        vve: "Je VvE",
        who: "Wie je bent",
        password: "Beveiliging",
        confirm: "Controleren",
      },
      intent: {
        heading: "Waar kom je voor?",
        lead: "Zo stemmen we de aanmelding af op jouw situatie.",
        board: "Ik start met mijn eigen VvE",
        board_hint: "Je richt Pandig in voor één VvE.",
        manager: "Ik beheer meerdere VvE's",
        manager_hint: "Begin met één VvE en voeg er later meer toe.",
        owner: "Ik ben eigenaar met een uitnodiging",
        owner_hint: "Je sluit je aan via de link uit je uitnodiging.",
        owner_notice_title: "Eigenaren melden zich aan via een uitnodiging",
        owner_notice_body: "Als eigenaar krijg je van je VvE een uitnodigingslink. Daarmee zie je je eigen bijdrage, machtiging en betalingen — je hoeft zelf geen VvE aan te maken.",
        owner_notice_login: "Inloggen",
        owner_notice_back: "Toch iets anders kiezen",
      },
      vve: {
        heading_board: "Hoe heet je VvE?",
        heading_manager: "Met welke VvE begin je?",
        lead_board: "De naam waaronder je VvE bij Pandig bekend staat.",
        lead_manager: "Je kunt er straks meer toevoegen.",
        manager_contact: "Meerdere VvE's? Vraag naar portefeuilleprijzen:",
        manager_contact_link: "Neem contact op",
      },
      who: {
        heading_board: "Wie ben jij binnen de VvE?",
        heading_manager: "Maak je eigen account aan",
        lead: "Hiermee log je straks in en beheer je de VvE.",
        existing_email: "Dit e-mailadres heeft al een account.",
        existing_email_link: "Inloggen",
      },
      password: {
        heading: "Beveilig je account",
        lead: "Kies een sterk wachtwoord van minimaal 15 tekens.",
        hint_blocklist: "Vermijd een veelgebruikt wachtwoord.",
        mismatch: "De wachtwoorden komen niet overeen.",
      },
      confirm: {
        heading: "Controleer en maak je account aan",
        vve_label: "VvE",
        email_label: "E-mailadres",
        trust_untouched: "Pandig raakt het geld niet aan — bijdragen gaan rechtstreeks naar de eigen rekening van de VvE.",
        trust_price: "€ 1 per eenheid per maand, excl. btw · maandelijks opzegbaar.",
        trust_mollie: "Incasso via Mollie, onder toezicht van De Nederlandsche Bank.",
        terms_label: "Akkoord met de algemene voorwaarden",
        terms_before: "Ik ben bevoegd om de organisatie te binden en ga namens haar akkoord met de ",
        terms_link: "algemene voorwaarden (versie 2026-08-08)",
        terms_after: ".",
      },
    },
  },
  forgot: {
    title: "Wachtwoord vergeten",
    heading: "Wachtwoord vergeten?",
    lead: "We sturen een herstellink naar je e-mailadres.",
    email: "E-mailadres",
    email_ph: "naam{'@'}voorbeeld.nl",
    submit: "Stuur herstellink",
    back: "Terug naar inloggen",
    sent_title: "Check je inbox",
    sent_body: "Bestaat er een account met dit adres, dan is de herstellink onderweg.",
  },
  reset: {
    title: "Nieuw wachtwoord",
    heading: "Nieuw wachtwoord",
    password: "Nieuw wachtwoord",
    password_hint: "Minimaal 15 tekens.",
    password_confirmation: "Herhaal wachtwoord",
    submit: "Wachtwoord opslaan",
    invalid_link: "Deze herstellink is ongeldig of verlopen.",
    success: "Je wachtwoord is bijgewerkt. Log in met je nieuwe wachtwoord.",
    back: "Terug naar inloggen",
  },
  verify_email: {
    title: "E-mailadres bevestigen",
    heading: "E-mailadres bevestigen",
    verifying: "We controleren je bevestigingslink…",
    success: "Je e-mailadres is bevestigd.",
    invalid: "Deze bevestigingslink is ongeldig of verlopen.",
    continue: "Verder naar Pandig",
  },
  organisations: {
    title: "Organisaties",
    heading: "Kies een organisatie",
    active: "Actief",
    // The badge only marks which organisation you're in; "Doorgaan" is what gets you
    // out of this page — without it a bestuurder with one VvE has nowhere to click.
    continue: "Doorgaan",
    switch: "Kies",
    empty: "Je bent nog niet aan een organisatie gekoppeld.",
    // Een VvE aanmaken kan alleen vanaf een uitgelogd scherm (/signup is guest-only),
    // dus uitloggen is hier de enige echte uitweg — en meteen de weg naar een nieuwe VvE.
    empty_hint: "Log uit om met een ander account in te loggen of een nieuwe VvE aan te maken.",
    sign_out: "Uitloggen",
    role: { owner: "Eigenaar", board: "Bestuur" },
  },
  nav: {
    group: "Menu",
    mode_org: "Organisatie",
    mode_personal: "Persoonlijk",
    switch_mode: "Wissel van weergave",
    overzicht: "Overzicht",
    eenheden: "Eenheden",
    team: "Team",
    opvolgen: "Opvolgen",
    facturen: "Facturen van Pandig",
    mijn_bijdrage: "Mijn bijdrage",
    settings: "Instellingen",
    sign_out: "Uitloggen",
    switch_org: "Wissel van organisatie",
    all_organisations: "Alle organisaties",
  },
  placeholder: {
    body: "Dit onderdeel komt binnenkort beschikbaar.",
  },
  table: {
    search: "Zoeken",
    filter: "Filter",
    from: "Vanaf",
    to: "Tot",
    clear: "Filters wissen",
    density: "Compacter weergeven",
    empty: "Geen resultaten.",
    empty_filtered: "Geen resultaten voor deze filters.",
    range: "{from}–{to} van {total}",
    prev: "Vorige",
    next: "Volgende",
  },
  eenheden: {
    // The header only has room for the noun; this says once, in place, what it covers —
    // open-ended on purpose, since a VvE can be a flat, a park or a row of houses.
    subtitle: "Appartementen, woningen, bedrijfsruimtes, vakantiehuizen — alles waarvoor de VvE een bijdrage int.",
    add: "Eenheid toevoegen",
    add_help: "Voeg de eenheid en de periodieke bijdrage toe.",
    cancel: "Annuleren",
    // Rij-acties zijn iconen met een hover-label (zoals bij Team), dus dit label moet
    // op zichzelf duidelijk zijn: "Wijzigen" alleen zegt niet wát er verandert.
    edit: "Bijdrage wijzigen",
    edit_for: "Wijzig bijdrage voor {unit}",
    edit_title: "Bijdrage wijzigen · {unit}",
    edit_help: "Het nieuwe bedrag en interval gelden vanaf de volgende incasso. De huidige incasso wordt veilig vervangen.",
    edit_preview: "Vanaf de volgende incasso",
    save: "Wijziging opslaan",
    updated: "De bijdrage voor {unit} is bijgewerkt vanaf de volgende incasso.",
    invite_owner: "Eigenaar uitnodigen",
    invite_owner_for: "Nodig een eigenaar uit voor {unit}",
    no_owner: "Geen eigenaar",
    // Shown beside a bestuurder's name in de eigenaar-keuzelijst: rol en eigenaarschap zijn
    // twee dingen, en een bestuurder aan een eenheid koppelen mag geen verrassing zijn.
    owner_role_board: "bestuur",
    empty: "Nog geen eenheden. Voeg je eerste appartement, woning of bedrijfsruimte toe om te beginnen.",
    col: {
      reference: "Eenheid",
      owner: "Eigenaar",
      amount: "Bijdrage",
      interval: "Interval",
      state: "Status",
    },
    state: {
      ok: "Klaar om te innen",
      no_owner: "Geen eigenaar",
      no_subscription: "Geen incasso",
      no_mandate: "Machtiging nodig",
      inactive: "Inactief",
    },
    owner_reassigned: "{owner} is nu eigenaar van {unit}.",
    owner_cleared: "Eigenaar losgekoppeld van {unit}.",
    invite_sent: "Uitnodiging verstuurd naar {email}.",
    // Eigenaar wijzigen gebeurt in een modal: het is een geldbeslissing, dus je kiest
    // eerst en ziet vóór het opslaan wat het met de incasso doet.
    owner_change: "Eigenaar wijzigen",
    owner_change_for: "Wijzig de eigenaar van {unit}",
    owner_change_title: "Eigenaar wijzigen · {unit}",
    owner_link: "Eigenaar koppelen",
    owner_link_for: "Koppel een eigenaar aan {unit}",
    owner_link_title: "Eigenaar koppelen · {unit}",
    owner_help: "De eigenaar is degene bij wie de bijdrage voor deze eenheid wordt afgeschreven.",
    owner_current: "Nu: {owner}",
    owner_search: "Zoek op naam",
    owner_no_match: "Geen naam gevonden.",
    owner_none_help: "Laat deze eenheid even zonder eigenaar.",
    owner_mandate_ready: "Machtiging geldig",
    owner_mandate_missing: "Geen machtiging",
    owner_save: "Eigenaar opslaan",
    owner_effect: "Wat er gebeurt",
    owner_effect_moved: "De lopende incasso stopt en gaat op naam van {owner} verder, vanaf de eerstvolgende incassodatum.",
    owner_effect_assigned: "Pandig zet de incasso voor {unit} op naam van {owner}.",
    owner_effect_cleared_running: "De lopende incasso stopt. Er wordt niets meer geïnd voor {unit} tot je een nieuwe eigenaar koppelt.",
    owner_effect_cleared: "Er wordt niets geïnd voor {unit} tot je een eigenaar koppelt.",
    owner_effect_inactive: "Deze eenheid is inactief, dus er wordt nog niets geïnd. Activeer de eenheid om de incasso te starten.",
    owner_effect_mandate_ready: "{owner} heeft een geldige machtiging, dus de incasso kan meteen door.",
    owner_effect_mandate_missing: "{owner} heeft nog geen geldige machtiging. Pandig start de incasso zodra die is afgegeven.",
    activation: {
      activate: "Activeren",
      activate_for: "Activeer {unit}",
      activate_title: "Eenheid activeren · {unit}",
      activate_help: "Deze eenheid telt weer mee voor de maandelijkse facturatie. Heeft de eigenaar een geldige machtiging, dan start de periodieke incasso direct.",
      activate_confirm: "Eenheid activeren",
      activated: "{unit} is geactiveerd en telt mee voor facturatie. Met een geldige machtiging start de incasso automatisch.",
      deactivate: "Deactiveren",
      deactivate_for: "Deactiveer {unit}",
      deactivate_title: "Eenheid deactiveren · {unit}",
      deactivate_help: "De lopende incasso van de eigenaar stopt en de eenheid telt niet meer mee voor de volgende maandfactuur. De betaalgeschiedenis blijft bewaard.",
      deactivate_confirm: "Eenheid deactiveren",
      deactivated: "{unit} is gedeactiveerd. De incasso is gestopt en de eenheid telt niet meer mee voor facturatie.",
    },
  },
  opvolgen: {
    empty: "Niets om op te volgen. Alle bijdragen zijn binnen of onderweg.",
    // Wat Pandig al deed, en wat het bestuur nog kan doen. Bewust nuchter: een mislukte
    // incasso is een uitzondering, geen incident.
    reminder_sent: "Herinnering verstuurd",
    unresolved: "Nog niet opgevolgd",
    retry: "Opnieuw proberen",
    retry_error: "Opnieuw innen is niet gelukt. Probeer het later nog eens.",
    only_followup: "Alleen opvolgen",
    all_payments: "Alle betalingen",
  },
  team: {
    title: "Team",
    heading: "Wie is er aangesloten",
    invite: "Uitnodigen",
    invite_email: "E-mailadres",
    invite_role: "Rol",
    invite_unit: "Eenheid",
    col: {
      email: "E-mailadres",
      role: "Rol",
      unit: "Eenheid",
      status: "Status",
      invited_by: "Uitgenodigd door",
    },
    status: {
      pending: "Uitgenodigd",
      accepted: "Actief",
      revoked: "Ingetrokken",
    },
    resend: "Opnieuw versturen",
    revoke: "Intrekken",
    remove: "Verwijderen",
    delete: "Verwijderen",
    // Twee verschillende dingen, nooit samengevoegd: iemand uit het team halen gaat over
    // toegang, gegevens verwijderen gaat over de AVG. Wie nog betaalhistorie heeft kan
    // niet uit het team, maar zijn gegevens kunnen wél weg — dat is de uitweg die
    // "er is nog activiteit aan gekoppeld" tot nu toe niet had.
    erase: "Gegevens verwijderen",
    erase_title: "Gegevens van {email} verwijderen",
    erase_body: "De gegevens van dit lid binnen deze VvE worden verwijderd: machtiging, rekeninghouder en IBAN. De lopende machtiging wordt bij de bank ingetrokken en overal waar tot nu toe hun naam stond, komt “Verwijderd lid” te staan. Betalingen blijven bewaard zolang de wet dat vraagt. Dit kan niet ongedaan worden gemaakt.",
    erase_note: "Het Pandig-account van dit lid blijft bestaan — dat verwijderen ze zelf. Ze krijgen hier bericht van, omdat het bestuur dit namens de VvE besluit.",
    erase_confirm: "Gegevens verwijderen",
    erase_cancel: "Annuleren",
    erase_success: "De gegevens van {email} zijn verwijderd.",
    empty: "Nog niemand uitgenodigd.",
    invite_success: "Uitnodiging verstuurd naar {email}.",
    resend_success: "E-mail opnieuw verstuurd naar {email}.",
    revoke_success: "Uitnodiging voor {email} ingetrokken.",
    remove_success: "{email} is verwijderd uit het team.",
    delete_success: "Uitnodiging voor {email} verwijderd.",
  },
  invitation: {
    title: "Uitnodiging",
    heading: "Je bent uitgenodigd bij {org}",
    has_account: "Bevestig met je bestaande wachtwoord om {org} toe te voegen.",
    no_account: "Maak je wachtwoord aan om aan de slag te gaan.",
    current_password: "Wachtwoord",
    name: "Naam",
    submit: "Meedoen",
    invalid_link: "Deze uitnodiging is ongeldig, verlopen of al gebruikt.",
    already_accepted: "Deze uitnodiging is al gebruikt.",
    back: "Terug naar inloggen",
  },
  facturen: {
    empty: "Nog geen facturen van Pandig.",
    failed_title: "Betaling aan Pandig niet gelukt",
    download: "Download pdf",
    download_for: "Download factuur {number} als pdf",
    billing: {
      eyebrow: "Betaling aan Pandig",
      price: "{fee} per actieve eenheid + {vat}% btw",
      monthly: "{count} actieve eenheden · {total} per maand",
      error: "We konden de betaalmachtiging niet starten. Probeer het opnieuw.",
      state: {
        none: {
          title: "Activeer facturatie",
          body: "Geef eenmalig toestemming via iDEAL. Daarna incasseert Pandig maandelijks automatisch voor je actieve eenheden en ontvang je een btw-factuur.",
          cta: "Geef toestemming via iDEAL",
        },
        pending: {
          label: "Wordt verwerkt",
          title: "Machtiging wordt verwerkt",
          body: "Mollie verwerkt je toestemming. Vernieuw deze pagina over een moment, of start iDEAL opnieuw als je de betaling niet hebt afgerond.",
          cta: "Opnieuw naar iDEAL",
        },
        active: {
          label: "Actief",
          title: "Facturatie is actief",
          body: "De maandelijkse incasso volgt automatisch het aantal actieve eenheden. Iedere afschrijving levert hier een btw-factuur op.",
        },
        ready: {
          label: "Machtiging actief",
          title: "Klaar voor actieve eenheden",
          body: "Je machtiging is geldig. Zodra je een eenheid activeert, start Pandig de maandelijkse facturatie automatisch.",
        },
        attention: {
          label: "Actie nodig",
          title: "Facturatie synchroniseren",
          body: "Je machtiging is geldig, maar het maandabonnement is nog niet actief. Probeer de synchronisatie opnieuw.",
          cta: "Synchroniseer opnieuw",
        },
        retry: {
          label: "Vernieuwen",
          title: "Vernieuw je machtiging",
          body: "De eerdere machtiging is niet meer geldig. Geef opnieuw toestemming via iDEAL om de facturatie te hervatten.",
          cta: "Opnieuw toestemming geven",
        },
      },
    },
    col: {
      number: "Factuur",
      period: "Periode",
      units: "Eenheden",
      per_unit: "Per eenheid",
      subtotal: "Subtotaal",
      vat: "BTW",
      total: "Totaal",
      status: "Status",
    },
  },
  charts: {
    trend: {
      title: "Per maand binnengekomen",
      paid: "Binnen",
      failed: "Nog niet gelukt",
      detail: "{month}: {paid} binnengekomen",
      detail_failed: "{month}: {paid} binnengekomen, {failed} nog niet gelukt",
    },
    empty: "Nog geen historie om te tonen.",
  },
  betalingen: {
    title: "Betalingen",
    empty: "Nog geen betalingen.",
    download: "Download pdf",
    download_for: "Download specificatie van de betaling van {date} als pdf",
    col: {
      date: "Datum",
      owner: "Eigenaar",
      unit: "Eenheid",
      amount: "Bedrag",
      status: "Status",
    },
  },
  betaling: {
    title: "Betaling",
    subtitle: "Details van deze betaling",
    timeline: "Verloop",
    settlement: {
      title: "Afwikkeling",
      until: "Definitief vanaf {date}",
    },
    event: {
      created: "Aangemaakt",
      paid: "Binnengekomen",
      failed: "Niet gelukt",
      charged_back: "Teruggeboekt",
      refunded: "Terugbetaald",
    },
  },
  dashboard: {
    title: "Overzicht",
    role: { owner: "Eigenaar", board: "Bestuur" },
    refresh: "Vernieuwen",
    retry: "Opnieuw proberen",
    error: "We konden je gegevens niet laden. Probeer het opnieuw — lukt het niet, neem dan contact op.",
    no_org: "Geen actieve organisatie",
    untouched: "Pandig raakt het geld niet aan",
    owners_count: "geen eigenaren | 1 eigenaar | {count} eigenaren",
    landed: "{amount} binnen",
    not_yet: "{count} nog niet gelukt",
    flow_to: "naar jullie rekening",
    next_run: "Onderweg naar jullie rekening",
    next_run_dated: "Onderweg naar jullie rekening · {date}",
    no_run_planned: "Nog geen run ingepland",
    segments: { expected: "Verwacht", landed: "Binnen", underway: "Nog onderweg" },
    setup: {
      title: "Nog niet klaar om te innen",
      no_owner: "geen eenheden zonder eigenaar | 1 eenheid zonder eigenaar | {count} eenheden zonder eigenaar",
      no_subscription: "geen eenheden zonder actieve incasso | 1 eenheid zonder actieve incasso | {count} eenheden zonder actieve incasso",
      no_mandate: "geen eigenaren zonder machtiging | 1 eigenaar zonder machtiging | {count} eigenaren zonder machtiging",
      hint: "Deze eenheden kunnen pas innen zodra je een eigenaar en machtiging koppelt.",
      all_good: "Alles staat klaar om te innen.",
    },
    coverage: {
      title: "Machtigingen",
      label: "{covered} / {total} actieve incasso's heeft een geldige machtiging",
      empty: "Nog geen actieve incasso's.",
    },
    followup: {
      title: "Opvolgen",
      mandate_needed: "Machtiging nodig",
      mandate_needed_desc: "geen eigenaren | 1 eigenaar geeft opnieuw toestemming | {count} eigenaren geven opnieuw toestemming",
      retry: "Opnieuw proberen",
      // Geen "heuristiek" meer: dit is dezelfde definitie als de lijst op /opvolgen en de
      // knop daar. Wat je hier ziet, kun je daar met één klik opnieuw aanbieden.
      retry_desc: "geen betalingen | 1 betaling kun je opnieuw aanbieden | {count} betalingen kun je opnieuw aanbieden",
      chargeback: "Terugboeking",
      chargeback_desc: "geen terugboekingen | 1 terugboeking om op te volgen | {count} terugboekingen om op te volgen",
      platform: "Je betaling aan Pandig is nog niet gelukt. Werk je betaalmethode bij om je abonnement actief te houden.",
      all_good: "Alles op koers. Alle bijdragen zijn binnen of onderweg.",
    },
    onboarding: {
      checklist_title: "Jullie VvE klaarzetten",
      progress: "{done} van {total} klaar",
      encourage: "Alles staat klaar. | Nog één stap en jullie kunnen innen. | Nog {count} stappen en jullie kunnen innen.",
      intro_link: "Intro",
      tour_link: "Rondleiding",
      toggle: "Stap open- of dichtklappen",
      status: { done: "Klaar", active: "Nu", waiting: "In afwachting", todo: "Later", attention: "Aandacht nodig" },
      announce: "{title} is klaar — nog {remaining} te gaan.",
      mollie_title: "Koppel jullie Mollie-account",
      mollie_why: "Jullie maken een eigen Mollie-account voor de VvE aan; Pandig koppelt daaraan en verzorgt de incasso's erdoor — als een soort plug-in. Het geld komt rechtstreeks op de rekening van de VvE. Pandig raakt het niet aan.",
      mollie_cta: "Verbind met Mollie",
      mollie_review: "Mollie controleert jullie gegevens. Dit duurt meestal maar even.",
      mollie_reconnect: "De koppeling met Mollie moet opnieuw. Klik om te herstellen.",
      mollie_done: "Het Mollie-account van de VvE is gekoppeld.",
      units_title: "Voeg je eenheden toe",
      units_why: "Een eenheid is alles waarvoor de VvE een bijdrage int: een appartement, een woning, een bedrijfsruimte, een vakantiehuis. Voeg ze toe zodat je per eenheid kunt innen.",
      units_cta: "Eenheid toevoegen",
      units_none: "Nog geen eenheden toegevoegd — appartementen, woningen of andere ruimtes.",
      units_done: "1 eenheid toegevoegd | {count} eenheden toegevoegd",
      owners_title: "Nodig eigenaren uit",
      owners_why: "Eigenaren tekenen eenmalig een digitale machtiging. Daarna wordt de bijdrage automatisch geïncasseerd. Jij nodigt ze uit; zij tekenen zelf.",
      owners_cta: "Eigenaren uitnodigen",
      owners_progress: "{signed} van {total} eigenaren hebben getekend",
      owners_remind: "Herinner eigenaren",
      owners_waiting: "Jouw deel is gedaan. Eigenaren tekenen hun machtiging in hun eigen tijd — je ziet het hier zodra ze klaar zijn.",
      owners_done: "Alle eigenaren hebben hun machtiging getekend.",
      billing_title: "Bevestig de rekening van de VvE",
      billing_why: "Geef Pandig eenmalig een machtiging vanaf de rekening van de VvE. Daarmee staat vast dat de rekening van jullie is, en verloopt de maandfactuur van Pandig later automatisch.",
      billing_cta: "Rekening bevestigen",
      // The step goes straight to Mollie, so these two lines say beforehand what used to
      // be visible on /facturen: the iDEAL screen shows an amount because the machtiging
      // is verified with a small payment, and the machtiging is what the monthly fee
      // later runs on. The full berekening stays on /facturen, one click away.
      billing_ideal: "Je geeft toestemming via iDEAL met een kleine verificatiebetaling.",
      billing_price: "Daarna kost Pandig € 1 per eenheid per maand, excl. btw.",
      billing_price_link: "Bekijk de berekening",
      billing_done: "De rekening van de VvE is bevestigd.",
      done_title: "Alles staat klaar",
      done_body: "Jullie VvE kan innen. Vanaf hier volg je alles op het overzicht.",
      connect_error_denied: "Je hebt het verbinden met Mollie geannuleerd. Klik gerust opnieuw op 'Verbind met Mollie' wanneer je zover bent.",
      connect_error_state: "De koppeling met Mollie is niet gelukt. Probeer het opnieuw.",
      connect_error_closed: "Deze VvE is gestopt bij Pandig, dus er valt niets te koppelen. Wil je opnieuw beginnen, gebruik dan “Weer starten” bij Instellingen.",
      connect_error_failed: "Er ging iets mis bij het verbinden met Mollie. Probeer het opnieuw — lukt het niet, neem dan contact op.",
      disconnect: "Mollie-koppeling verbreken",
      disconnect_confirm: "Weet je zeker dat je de Mollie-koppeling wilt verbreken?",
    },
    welcome: {
      title: "Welkom — {org} staat klaar",
      aria_label: "Welkom bij Pandig",
      intro: "Bijdragen worden automatisch geïncasseerd en komen rechtstreeks op jullie eigen rekening.",
      steps_intro: "In vier stappen kunnen jullie innen:",
      reassure: "Je hoeft niet alles vandaag te doen — stap voor stap komt goed.",
      start: "Aan de slag",
      skip: "Sla over",
      next: "Volgende",
      back: "Vorige",
      step_of: "Stap {current} van {total}",
    },
    tour: {
      title: "Rondleiding door Pandig",
      start: "Start de rondleiding",
      link: "Rondleiding",
      overzicht: "Hier volg je de stappen om jullie VvE klaar te zetten. Zodra alles loopt, zie je hier hoe de incasso ervoor staat.",
      eenheden: "Een eenheid is alles waarvoor de VvE een bijdrage int — een appartement, woning of bedrijfsruimte. Hier beheer je per eenheid de eigenaar en de bijdrage, en zie je of de incasso kan lopen.",
      team: "Nodig hier je medebestuurders en de eigenaren uit. Eigenaren krijgen zo hun eigen plek om de machtiging te regelen en hun bijdrage te volgen — daar hoef jij niet achteraan.",
      opvolgen: "Deze lijst is meestal leeg — zo hoort het. Lukt een afschrijving een keer niet, dan staat die hier, heeft Pandig de eigenaar er al zelf over gemaild en biedt Pandig de bijdrage een paar werkdagen later automatisch nog één keer aan. Wil je niet wachten, dan doe je dat hier met één klik.",
      facturen: "Hier vind je wat de VvE aan Pandig betaalt voor het gebruik: een klein vast bedrag per actieve eenheid per maand. Dit staat helemaal los van de bijdragen van eigenaren — die gaan rechtstreeks naar de rekening van de VvE.",
      modes: "Hier wissel je tussen twee kanten: Organisatie is het bestuur aan het werk voor de hele VvE, Persoonlijk gaat over jouw eigen bijdrage. We nemen nu een kijkje bij Persoonlijk.",
      // Eén zin die de drie oude stops dekt: de persoonlijke kant is één pagina.
      mijn_bijdrage: "Dit is jouw eigen kant: hier geef je één keer toestemming voor de incasso, zie je wat er wanneer wordt afgeschreven, en vind je al je betalingen terug.",
      progress: "{current} van {total}",
      next: "Volgende",
      prev: "Vorige",
      skip: "Overslaan",
      close: "Rondleiding sluiten",
    },
    security_setup: {
      title: "Maak je account veiliger",
      body: "Rond deze stappen af om je account goed te beveiligen.",
      verify_email: "E-mailadres bevestigen",
      enable_2fa: "Tweestapsverificatie of passkey instellen",
      email_sent: "We hebben een nieuwe bevestigingslink naar {email} gestuurd. Klik op de link om je e-mailadres te bevestigen.",
      error: "Versturen is niet gelukt. Probeer het zo nog eens.",
      dismiss: "Melding sluiten",
    },
    // Waar deze VvE staat met Pandig, op elk scherm. Geen foutmelding: opgezegd is een
    // gewone maand die gewoon doorloopt, gestopt is een archief dat je mag lezen.
    lifecycle: {
      leaving: {
        title: "Opgezegd",
        body: "De incasso's zijn gestopt. Pandig loopt nog tot {date} — tot die datum verandert er verder niets en kun je dit ongedaan maken.",
        action: "Toch blijven",
      },
      closed: {
        title: "Gestopt bij Pandig",
        body: "Deze VvE is gestopt. Je kunt alles blijven inzien en downloaden, maar er valt niets meer te wijzigen.",
        action: "Weer starten",
      },
    },
  },
  // De hele Persoonlijk-kant, op één pagina. De eigenaar heeft drie vragen — is het
  // geregeld, wat betaal ik waarvoor, en is het goed gegaan — en die worden hier in die
  // volgorde beantwoord. Toets voor elke zin in blok ①: is duidelijk wie aan zet is? Is
  // dat de eigenaar, dan staat er een knop en geen verwijzing naar het bestuur.
  owner: {
    title: "Mijn bijdrage",
    refresh: "Vernieuwen",
    retry: "Opnieuw proberen",
    error: "We konden je gegevens niet laden. Probeer het opnieuw — lukt het niet, neem dan contact op.",
    action_error: "Dat lukte niet. Probeer het zo nog eens — blijft het misgaan, neem dan contact op.",
    // ① De stand van zaken: één kop, één zin, hooguit één knop.
    status_title: "De stand van zaken",
    state: {
      no_units: {
        title: "Hier komt jouw bijdrage",
        body: "Je bent nog niet aan een eenheid gekoppeld, dus er valt nog niets te innen. Zodra je bestuur je aan je eenheid koppelt, staat hier wat je betaalt en wanneer.",
        // Een bestuurder bezit vaak zelf een appartement in de VvE die hij bestuurt
        // (PERSONAS.md §1) — die kan dit zelf oplossen, dus krijgt geen verwijzing.
        board_body: "Bezit je zelf een eenheid in deze VvE? Koppel jezelf bij Eenheden aan je eigen eenheid, dan regel je je machtiging hier net als elke andere eigenaar.",
        board_cta: "Ga naar Eenheden",
      },
      needs_mandate: {
        title: "Nog één ding: geef toestemming",
        body: "Je ondertekent één keer een machtiging via een verificatiebetaling van één cent. Daarna wordt je bijdrage automatisch geïncasseerd. Zonder geldige machtiging schrijft Pandig niets af.",
        cta: "Geef toestemming",
      },
      mandate_expired: {
        title: "Je machtiging is verlopen",
        body: "Je vorige machtiging geldt niet meer, dus er wordt op dit moment niets afgeschreven. Geef opnieuw toestemming en je bijdrage loopt weer.",
        cta: "Geef opnieuw toestemming",
      },
      awaiting_bank: {
        title: "We verwerken je machtiging",
        body: "Je bank is er mee bezig. Dat duurt meestal maar een paar minuten — deze pagina werkt zichzelf bij zodra je terugkomt.",
      },
      retry_mandate: {
        title: "De vorige poging lukte niet",
        body: "Er ging iets mis bij het verwerken van je verificatiebetaling. Er is niets afgeschreven, dus je kunt het gerust opnieuw proberen.",
        cta: "Opnieuw proberen",
      },
      verifying: {
        title: "Je machtiging is geregeld",
        body: "Je machtiging staat klaar. Bij de eerste afschrijving wordt hij door je bank definitief bevestigd.",
        body_dated: "Je eerste bijdrage staat klaar voor {date}. Bij die afschrijving wordt je machtiging door je bank definitief bevestigd.",
      },
      no_subscription: {
        title: "Je bestuur stelt je bijdrage nog in",
        body: "Je machtiging is geregeld, dus jouw deel is gedaan. Zodra je bestuur de bijdrage voor je eenheid instelt, zie je hier wanneer en hoeveel er wordt afgeschreven.",
      },
      scheduled: {
        title: "Je bijdrage loopt",
      },
      failed_last: {
        title: "De bijdrage van {month} is niet gelukt",
        // Hand-synced met dues_mailer.payment_failed.failed.next: de mail linkt hierheen,
        // dus pagina en mail moeten hetzelfde vervolg beloven — niet meer en niet minder
        // dan PaymentRetryJob echt doet.
        body: "Je machtiging blijft geldig, dus je hoeft niets opnieuw te ondertekenen. We hebben je hierover gemaild en proberen de afschrijving over een paar werkdagen automatisch nog één keer. Zorg dat er dan voldoende saldo op je rekening staat.",
        reversed_title: "De bijdrage van {month} is teruggeboekt",
        reversed_body: "Het geld is naar je rekening teruggegaan, dus de bijdrage van deze periode staat nog open. Omdat de terugboeking van jouw kant kwam, bieden we die niet automatisch opnieuw aan — je bestuur neemt hierover contact met je op.",
      },
    },
    countdown_in_days: "over {count} dag | over {count} dagen",
    countdown_today: "vandaag",
    countdown_line: "{when} — {date} · {amount} van {iban}",
    countdown_notice: "Je ontvangt hierover ook bericht.",
    // ② Wat je betaalt.
    pay: {
      title: "Wat je betaalt",
      from_account: "Van rekening {iban}",
      signed_since: "Machtiging geldig sinds {date}",
      // Nooit voorbarig groen: tot een afschrijving is gelukt is de machtiging afgegeven,
      // niet bevestigd.
      signed_since_unconfirmed: "Machtiging afgegeven op {date}, wordt bevestigd bij de eerste afschrijving",
      empty: "Nog geen eenheden gekoppeld.",
    },
    // ③ Je betalingen.
    payments: {
      title: "Je betalingen",
      empty: "Nog geen betalingen. Zodra de eerste bijdrage is afgeschreven, staat die hier.",
      show_all: "Alle betalingen tonen",
      detail_for: "Bekijk de betaling van {date}",
    },
    // ④ Eerdere machtigingen — de meeste eigenaren zien dit nooit.
    history: { title: "Eerdere machtigingen" },
  },
  status: {
    paid: "Betaald",
    in_progress: "In behandeling",
    failed: "Nog niet gelukt",
    charged_back: "Teruggeboekt",
    refunded: "Terugbetaald",
    canceled: "Geannuleerd",
    valid: "Geldig",
    invalid: "Ongeldig",
    provisional: "Voorlopig binnen",
    final: "Definitief",
  },
  settings: {
    lead: "Beheer je gegevens, inloggegevens en documenten op één plek.",
    tabs: {
      profiel: "Profiel",
      account: "Account",
      beveiliging: "Beveiliging",
      verwerkersovereenkomst: "Verwerkersovereenkomst",
      organisatie: "Organisatie",
    },
    profiel: {
      heading: "Persoonsgegevens",
      name: "Naam",
      address: "Adres",
      address_ph: "Kerkstraat 12, 1234 AB Amsterdam",
      submit: "Profiel opslaan",
      success: "Je gegevens zijn bijgewerkt.",
    },
    password: {
      heading: "Wachtwoord",
      current: "Huidig wachtwoord",
      password: "Nieuw wachtwoord",
      password_hint: "Minimaal 15 tekens.",
      password_confirmation: "Herhaal wachtwoord",
      submit: "Wachtwoord opslaan",
    },
    email: {
      heading: "E-mailadres",
      email: "Nieuw e-mailadres",
      email_ph: "naam{'@'}voorbeeld.nl",
      current: "Wachtwoord",
      submit: "E-mailadres opslaan",
    },
    verify_email: {
      heading: "Bevestig je e-mailadres",
      body: "We hebben een bevestigingslink naar {email} gestuurd. Klik op die link, dan is je e-mailadres bevestigd.",
      required: "Dat is nodig voordat je de betaalinstellingen kunt wijzigen.",
      resend: "Bevestigingsmail opnieuw versturen",
    },
    account: {
      email_success: "We hebben een bevestigingslink naar je nieuwe e-mailadres gestuurd. Je huidige adres blijft actief tot je bevestigt.",
      pending_email: "Wacht op bevestiging van {email}.",
      password_success: "Je wachtwoord is bijgewerkt.",
    },
    // Verwijderen (AVG art. 17). Twee gevaarzones, allebei op één plek uitgeschreven:
    // wat er weggaat, wat er blijft en waarom, en pas daarna de knop. Bewust geen
    // dialoog die je wegklikt — de gevolgen passen er niet in en horen niet geskimd.
    // Kopie van je gegevens (AVG art. 15 inzage en art. 20 dataportabiliteit), en dezelfde
    // kaart voor de eigen kopie van de VvE (verwerkersovereenkomst §11). Staat vlak boven
    // "verwijderen", omdat een kopie opvragen de stap ervóór is.
    export: {
      contents_heading: "Wat je krijgt",
      contents: "Een zip-bestand met dezelfde gegevens twee keer: een csv om te openen in Excel of Numbers, en een json voor als je ze ergens anders wilt inlezen. Er zit een leesmij bij die uitlegt wat waar staat. Een onderdeel met alleen een koprij betekent dat we daarvan niets hebben.",
      excluded_heading: "Wat er niet in staat",
      // Dezelfde drie grenzen die ook in het bestand zelf staan — nooit meer beloven dan
      // we waarmaken. De bewaar-/Mollie-zin is dezelfde als bij verwijderen (erasure.kept).
      limits: "Het bestand is een momentopname: wat we hadden toen we het klaarzetten. Back-ups worden volgens de normale cyclus overschreven, en wat Mollie zelf over betalingen bewaart valt onder hun eigen bewaarplicht.",
      request: "Kopie aanvragen",
      download: "Kopie downloaden",
      available_until: "Te downloaden tot {date}.",
      opens_again: "Een nieuwe kopie kun je vanaf dan weer aanvragen.",
      requested_by: "Aangevraagd door {name} op {date}.",
      too_soon: "Er is deze week al een kopie opgevraagd. Vanaf {date} kan het weer.",
      account: {
        heading: "Kopie van mijn gegevens",
        lead: "Vraag alles op wat Pandig van jou heeft: je account, en per VvE je lidmaatschap, machtigingen, eenheden, incasso’s en betalingen. Je krijgt een mail zodra de kopie klaarstaat; je hoeft niet te wachten op deze pagina. Dat kan één keer per week.",
        excluded: "Je wachtwoord, tweestapscodes en passkey-sleutels staan er niet in — een kopie van de sleutels van je account is geen kopie van je gegevens. Gegevens van anderen ook niet: uitnodigingen die jij hebt verstuurd, gaan over het e-mailadres van iemand anders.",
        preparing: "We zetten de kopie klaar. Je krijgt een mail zodra hij klaar is.",
      },
      organisatie: {
        heading: "Kopie van de gegevens van de VvE",
        lead: "Vraag alles op wat Pandig voor deze VvE verwerkt: leden, eenheden, machtigingen, incasso’s, betalingen, uitnodigingen en de facturen van Pandig. Dit is de eigen administratie van de VvE, geen bestand om aan één eigenaar door te sturen — wil een eigenaar zijn eigen gegevens, dan vraagt hij die aan in zijn eigen account. Dat kan één keer per week per VvE.",
        excluded: "Dit bestand bevat de gegevens van álle leden. Koppelgegevens met Mollie staan er niet in, en van een lid waarvan de gegevens zijn verwijderd staat er “Verwijderd lid” in plaats van een naam.",
        preparing: "We zetten de kopie klaar. Elk bestuurslid krijgt een mail zodra hij klaar is.",
      },
      // Naast de danger zone van de Organisatie-tab: niemand mag een VvE verwijderen in de
      // veronderstelling dat er een bestand klaarstaat dat er niet is.
      status_none: "Er is nog geen kopie van de gegevens opgevraagd.",
      status_preparing: "Er wordt een kopie klaargezet.",
      status_ready: "Kopie klaargezet op {prepared}, te downloaden tot {expires}.",
    },
    erase: {
      heading: "Account verwijderen",
      lead: "Je verwijdert je Pandig-account: je naam, adres, inloggegevens, passkeys en actieve sessies. Lopende machtigingen worden bij de bank ingetrokken. Dit kan niet ongedaan worden gemaakt.",
      kept_heading: "Wat blijft er staan",
      kept: "Betalingen en facturen blijven bewaard zolang de wet dat vraagt (7 jaar na het einde van het relevante boekjaar) en verdwijnen daarna vanzelf. Bij die betalingen staat vanaf nu “Verwijderd lid” in plaats van je naam. Back-ups worden binnen 30 dagen overschreven. Wat Mollie zelf over betalingen bewaart, valt onder hun eigen bewaarplicht.",
      blocked_heading: "Dit staat nog open",
      confirm_label: "Typ je e-mailadres om te bevestigen",
      submit: "Account definitief verwijderen",
    },
    // Stoppen met Pandig zonder iets kwijt te raken — de belofte "maandelijks opzegbaar"
    // van de homepage. Staat bewust vóór het verwijderen hieronder, en in gewone taal:
    // dit is een zakelijke keuze, geen noodknop.
    opzeggen: {
      heading: "Opzeggen",
      lead_open: "Je stopt het gebruik van Pandig voor deze VvE. Er wordt niets verwijderd — je gegevens, betalingen en facturen blijven staan en blijven te downloaden.",
      lead_leaving: "Deze VvE is opgezegd. De incasso's zijn gestopt; tot de einddatum kun je alles blijven gebruiken en de opzegging bovenaan nog intrekken.",
      lead_closed: "Deze VvE is gestopt bij Pandig. Je kunt alles blijven inzien en downloaden. Wil je opnieuw beginnen, gebruik dan “Weer starten” bovenaan.",
      effects_heading: "Wat er gebeurt",
      effect_run: "De incasso's van alle eigenaren stoppen meteen — er wordt niets meer afgeschreven.",
      effect_period: "Het abonnement loopt tot het einde van de maand die je al betaald hebt. Er wordt niet naar rato terugbetaald.",
      effect_followup: "Een mislukte incasso van de laatste ronde kun je daarna niet meer opnieuw aanbieden. Rond Opvolgen eerst af als daar nog iets openstaat.",
      effect_mandates: "Op de einddatum trekken we alle machtigingen in en verbreken we de koppeling met Mollie. Kom je later terug, dan moet iedere eigenaar opnieuw tekenen.",
      effect_kept: "Je gegevens, betalingen en facturen blijven staan. Verwijderen is een aparte keuze, hieronder.",
      effect_undo: "Tot de einddatum kun je de opzegging weer intrekken.",
      submit: "VvE opzeggen",
    },
    organisatie: {
      heading: "VvE verwijderen",
      lead: "Je beëindigt het gebruik van Pandig door {org}. Dit geldt voor iedereen in de VvE, niet alleen voor jou, en kan niet ongedaan worden gemaakt.",
      effects_heading: "Wat er gebeurt",
      effect_run: "Lopende incasso’s worden gestopt.",
      effect_mandates: "Machtigingen van eigenaren worden ingetrokken.",
      effect_platform: "Het abonnement van Pandig wordt opgezegd en de machtiging daarvoor ingetrokken.",
      effect_members: "De persoonsgegevens van alle leden worden verwijderd.",
      kept_heading: "Wat blijft er staan",
      kept: "Betalingen en facturen blijven bewaard zolang de wet dat vraagt (7 jaar na het einde van het relevante boekjaar) en verdwijnen daarna vanzelf. Een openstaande factuur van Pandig blijft verschuldigd — we houden geen gegevens vast om die te innen. Back-ups worden binnen 30 dagen overschreven. Wat Mollie zelf over betalingen bewaart, valt onder hun eigen bewaarplicht.",
      confirm_label: "Typ de naam van de VvE om te bevestigen",
      submit: "VvE definitief verwijderen",
    },
    dpa: {
      heading: "Verwerkersovereenkomst",
      lead: "Pandig verwerkt persoonsgegevens namens jouw organisatie — vastgelegd in de verwerkersovereenkomst, verplicht volgens de AVG. Wil je die op naam van je organisatie, onderteken door Pandig? Vraag hieronder een exemplaar aan.",
      view: "Bekijk de overeenkomst",
      request: "Ondertekend exemplaar aanvragen",
    },
  },
  security: {
    reauthentication: {
      heading: "Bevestig dat jij het bent",
      lead: "Bevestig opnieuw voordat je passkeys, tweestapsverificatie, back-upcodes of sessies wijzigt.",
      // The dialog version (ReauthenticationDialog): it can come up on any page, so it
      // says why the question is being asked here and now, and promises the reader gets
      // to carry on where they were — which is exactly what happens.
      step_up_lead: "Deze stap gaat over geld of over de beveiliging van je account. Bevestig even dat jij het bent, dan gaan we direct verder.",
      cancel: "Annuleren",
      password: "Huidig wachtwoord",
      code: "Verificatiecode of back-upcode",
      submit: "Bevestigen",
      passkey: "Bevestigen met passkey",
      success: "Je identiteit is opnieuw bevestigd.",
    },
    passkeys: {
      heading: "Passkeys",
      lead: "Log zonder wachtwoord in met een passkey, bijvoorbeeld via Face ID, Touch ID of Windows Hello.",
      label_ph: "Naam van deze passkey (bijv. “MacBook van Quint”)",
      add: "Toevoegen",
      empty: "Je hebt nog geen passkeys toegevoegd.",
      created_at: "Toegevoegd op",
      delete: "Verwijderen",
      added: "Passkey “{label}” toegevoegd.",
      removed: "Passkey “{label}” verwijderd.",
    },
    two_factor: {
      heading: "Tweestapsverificatie",
      lead: "Voeg een extra beveiligingslaag toe met een authenticator-app.",
      enable: "Inschakelen",
      enabled: "Tweestapsverificatie is ingeschakeld.",
      replace: "Authenticator vervangen",
      disable: "Uitschakelen",
      disabled: "Tweestapsverificatie is uitgeschakeld.",
      disable_password_label: "Bevestig je wachtwoord om uit te schakelen",
      cancel: "Annuleren",
      setup_lead: "Scan deze QR-code met je authenticator-app, of voer de code hieronder handmatig in.",
      code_label: "Verificatiecode",
      code_ph: "123456",
      confirm: "Bevestigen",
      regenerate_backup_codes: "Nieuwe back-upcodes genereren",
      backup_codes_regenerated: "Nieuwe back-upcodes gegenereerd.",
      backup_codes_lead: "Bewaar deze codes op een veilige plek. Elke code werkt eenmalig als je geen toegang hebt tot je authenticator-app.",
      backup_codes_done: "Ik heb ze bewaard",
    },
    sessions: {
      heading: "Ingelogde apparaten",
      current: "Dit apparaat",
      last_active: "Laatst actief",
      revoke: "Andere sessies intrekken",
      revoke_one: "Intrekken",
      revoked: "Sessie ingetrokken.",
      revoked_all: "Alle andere sessies zijn ingetrokken.",
    },
  },
};
